import { createSelector } from 'reselect';
import * as proformaCompMinAvgMaxService from 'services/proformaCompMinAvgMaxService';
import { appSelectors } from 'store/app';
import { clientTxnOppsSelectors } from 'store/clientTxnOpps';
import { channelWorkflowSelectors } from 'store/underwrite/channelWorkflow';
import { propertyUWSelectors } from 'store/underwrite/propertyUW';
import { auctionUWSelectors } from 'store/underwrite/auctionUW';
import { queueSelectors } from 'store/queue';
import { importSelectors } from 'store/import/import';
import { exploreDataSelectors } from 'store/explore/data';
import { exploreShapesDataSelectors } from 'store/explore/shapesData';
import { exploreMapSelectors } from 'store/explore/map';
import { adminSelectors } from 'store/admin';
import { dashboardSelectors } from 'store/dashboard';
import { CheckTaskCompleted, PostAcqCheckTaskCompleted } from 'services/taskManagementService';
import { assumptionModulesSelectors } from 'store/assumptions/assumptionModules';
import { assumptionGroupsSelectors } from './assumptions/assumptionGroups';
import { exploreViewsSelectors } from './explore/views';
import { postAcqUWSelectors } from 'store/postAcq/postAcqUW';
import { postAcqWorkflowSelectors } from 'store/postAcq/postAcqWorkflow';
import { Constants } from 'utilities/constants';
const mapCompTypeToEnum = (compTypeString) => {
  switch (compTypeString) {
    case 'rent':
      return 0;
    case 'sale':
      return 1;
    case 'sold':
      return 2;
  }
};

//#region
// Selectors defined in slices when their inputs consist of only that slice's state
export const {
  userSelector,
  userRoleAuctionUnderwriteSelector,
  userRoleUnderwriteManagerSelector,
  userRoleExploreSelector,
  userRoleSourcingUserSelector,
  userRoleSourcingManagerSelector,
  servicesStatusSelector,
  appSettingsSelector,
  signalRQueuedUpdatesSelector,
} = appSelectors;

export const {
  clientTxnOppsSelector,
  isFetchingClientTxnOppsDataSelector,
} = clientTxnOppsSelectors;

export const {
  channelWorkflowStateSelector,
  channelWorkflowGridSelectedLayoutIdSelector,
  channelWorkflowGridOldestLayoutIdSelector,
} = channelWorkflowSelectors;

export const {
  propertyUWStateSelector,
  imagesSelector,
  imageRequestsSelector,
  transientValuationSelector,
  selectedValuationSelector,
  shouldCalculateProformaSelector,
  selectedOpportunityCompsSelector,
  filteredSelectedOpportunityCompsSelector,
  selectedProformaInputsContainerSelector,
  isFetchingPropertyUWDataSelector,
  isSyncingPurchaseStateSelector,
  selectedContainerIdSelector
} = propertyUWSelectors;

export const {
  selectedAuctionTxnOppsSelector,
  viewValuationDialogIsOpenSelector,
  auctionTxnOppsSelector,
  uwTasksSelector,
  driveByInspectionsSelector,
  auctionTitlesSelector,
  isFetchingAuctionUWDataSelector,
} = auctionUWSelectors;

export const {
  queueTxnOppsSelector,
  isFetchingQueueTxnOppsDataSelector,
  queueBinsStateSelector,
  queueSortInputSelector,
  originalMyTasksOrderSelector,
  showPostAcquisitionsSelector,
} = queueSelectors;

export const {
  importStateSelector,
  importModuleStateSelector,
  importTypeStateSelector,
  opportunityImportSelectedChannelSelector,
  canImportSelector,
  postAcqTaskImportWorkstreamTypeSelector
} = importSelectors;

export const {
  exploreDataSelector,
  propertyDataObjectByIdSelector,
  lastSelectedClusterSelector,
  propertyDataParamsSelector,
  exploreSelectedIdsSelector,
  exploreSelectedPropertiesCountSelector,
  exploreSelectedClusterIDsSelector,
  exploreSelectedClusterNamesSelector,
  explorePropertyDetailsDataSelector,
  isMainDataLoadingSelector,
} = exploreDataSelectors;

export const { shapesDataParamsSelector } = exploreShapesDataSelectors;

export const { exploreMapSelector, exploreColorMatrixObjSelector } = exploreMapSelectors;

export const { exploreViewsStateSelector, exploreViewsSelector, exploreSelectedViewSelector } = exploreViewsSelectors;

export const { mainAppMessageSelector, processingItemsServiceStatusSelector } = adminSelectors;

export const { selectedModuleSelector, modulesSelector } =
  assumptionModulesSelectors;

export const {
  selectedGroupSelector,
  customShapesSelector,
  selectedCustomShapeSelector,
} = assumptionGroupsSelectors;

export const {
  rentalInventorySelector,
  retailInventorySelector,
  dashboardChannelsSelector,
  dashboardFilterSelector,
} = dashboardSelectors;

export const {
  postAcqUWStateSelector,
  postAcqTransientValuationSelector,
  postAcqUWCompsImageCacheSelector,
  postAcqUWCompsImageRequestsSelector,
  filteredSelectedPostAcqOpportunityCompsSelector,
  modalFilteredSelectedPostAcqOpportunityCompsSelector,
  selectedPostAcqOpportunityCompsSelector,
  selectedPostAcqValuationSelector,
  isFetchingPostAcqUWDataSelector
} = postAcqUWSelectors;

export const {
  postAcqWorkflowGridSelectedLayoutIdSelector,
  postAcqWorkflowGridOldestLayoutIdSelector,
  isFetchingPostAcqWorkflowDataSelector
} = postAcqWorkflowSelectors;
//#endregion

//#region
// Selectors for state not contained in a slice, or when inputs come from multiple slices' state
export const selectedTaskSelector = createSelector(
  (state) => state.selectedTask,
  (selectedTask) => selectedTask
);

export const selectedOpportunitySelector = createSelector(
  [
    (state) => state.propertyUWState.propertyUWClientTxnOpp,
  ],
  (
    propertyUWClientTxnOpp,
  ) => {

    return propertyUWClientTxnOpp;
  }
);
export const selectedPostAcqTaskSelector = createSelector(
  [
    (state) => state.postAcqUWState.postAcqUWClientTask,
  ],
  (
    postAcqUWClientTask,
  ) => {

    return postAcqUWClientTask;
  }
);

export const propertyCTSelector = createSelector(
  // Find the census tract of selectedOpportunity's subject property
  [
    selectedOpportunitySelector,
    (state) => state.propertyUWState.selectedOpportunityCensusTracts,
  ],
  (selectedOpportunity, selectedOpportunityCensusTracts) => {
    return selectedOpportunityCensusTracts?.find((ct) => {
      // collection of all census tracts
      return ct.Geo === selectedOpportunity?.censusTractGeoId; // single census tract id that subject property is located in
    });
  }
);
export const isUsersValuationSelector = createSelector(
  [
    (state) => state.propertyUWState.selectedValuation,
    (state) => state.appState.user,
  ],
  (selectedValuation, user) => {
    return selectedValuation?.user
      ? selectedValuation.user.id === user.id
      : true;
  }
);
export const isUsersPostAcqValuationSelector = createSelector(
  [
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.selectedValuation : state.postAcqUWState.selectedValuation,
    (state) => state.appState.user,
  ],
  (selectedValuation, user) => {
    return selectedValuation?.user
      ? selectedValuation.user.id === user.id
      : true;
  }
);
export const displayedValuationSelector = createSelector(
  // Find which valuation to display and act on. When user is viewing their own, we use the transient to show any pending changes, rather than the persisted (selectedValuation). When user is viewing other users, we show the persisted.
  [
    isUsersValuationSelector,
    (state) => state.propertyUWState.transientValuation,
    (state) => state.propertyUWState.selectedValuation,
  ],
  (isUsersValuation, transientValuation, selectedValuation) => {
    return isUsersValuation ? transientValuation : selectedValuation;
  }
);

export const displayedPostAcqValuationSelector = createSelector(
  // Find which valuation to display and act on. When user is viewing their own, we use the transient to show any pending changes, rather than the persisted (selectedValuation). When user is viewing other users, we show the persisted.
  [
    isUsersPostAcqValuationSelector,
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.transientValuation : state.postAcqUWState.transientValuation,
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.selectedValuation : state.postAcqUWState.selectedValuation,
  ],
  (isUsersValuation, transientValuation, selectedValuation) => {
    return isUsersValuation ? transientValuation : selectedValuation;
  }
);

export const selectedProformaInputsSelector = createSelector(
  // Selector for calculating the min/avg/max values of a valuation's selected comps
  [
    displayedValuationSelector,
    (state) => state.propertyUWState.propertyUWClientTxnOpp,
    selectedOpportunityCompsSelector,
    (state) => state.propertyUWState.selectedProformaInputsContainer
  ],
  (
    displayedValuation,
    selectedOpportunity,
    selectedOpportunityComps,
    selectedProformaInputsContainer
  ) => {

    if (
      !selectedOpportunity ||
      !displayedValuation ||
      !selectedProformaInputsContainer
    )
      return null;
    // Taxes proforma radio items
    const taxOptions = [];
    if (selectedProformaInputsContainer.taxProformaInputs) {
      selectedProformaInputsContainer.taxProformaInputs.forEach((item) =>
        taxOptions.push(item)
      );
    }
    // HOA proforma radio items
    const hoaOptions = [];
    if (selectedProformaInputsContainer.taxProformaInputs) {
      selectedProformaInputsContainer.hoaProformaInputs.forEach((item) =>
        hoaOptions.push(item)
      );
    }

    // Repair proforma radio items
    const repairEstimateOptions = [];
    if (selectedProformaInputsContainer.repairProformaInputs) {
      selectedProformaInputsContainer.repairProformaInputs.forEach((item) =>
        repairEstimateOptions.push(item)
      );
    }

    // Rent proforma radio items
    // Get full collection of comps, filter down to rent and only those that have been selected by the valuation user
    const rentComps = selectedOpportunityComps.filter(
      (c) =>
        c.compType === mapCompTypeToEnum('rent') &&
        displayedValuation.valuationsComps.find((vc) => vc.compId === c.id) !=
          null
    );
    // Then use service to calculate the min/avg/max and return a values object array as rent proforma radio items
    const rentOptions = proformaCompMinAvgMaxService.create(
      rentComps,
      'rents',
      'rent'
    );
    if (selectedProformaInputsContainer.rentProformaInputs) {
      selectedProformaInputsContainer.rentProformaInputs.forEach((item) =>
        rentOptions.push(item)
      );
    }

    // ARV proforma radio items
    // Get full collection of comps, filter down to sold and only those that have been selected by the valuation user
    const soldComps = selectedOpportunityComps.filter(
      (c) =>
        c.compType === mapCompTypeToEnum('sold') &&
        displayedValuation.valuationsComps.find((vc) => vc.compId === c.id) !=
          null
    );
    // Then use service to calculate the min/avg/max and return a values object array as ARV proforma radio items
    const afterRepairValueOptions = proformaCompMinAvgMaxService.create(
      soldComps,
      'afterRepairValues',
      'arv'
    );
    if (selectedProformaInputsContainer.arvProformaInputs) {
      selectedProformaInputsContainer.arvProformaInputs.forEach((item) =>
        afterRepairValueOptions.push(item)
      );
    }

    // Closing costs proforma radio items
    const closingCostsOptions = [];
    if (selectedProformaInputsContainer.closingCostProformaInputs) {
      selectedProformaInputsContainer.closingCostProformaInputs.forEach(
        (item) => closingCostsOptions.push(item)
      );
    }

    // Bid proforma radio items
    const bidPriceOptions = [];
    if (selectedProformaInputsContainer.bidPriceProformaInputs) {
      selectedProformaInputsContainer.bidPriceProformaInputs.forEach((item) =>
        bidPriceOptions.push(item)
      );
    }

    return {
      hoas: hoaOptions,
      taxes: taxOptions,
      repairEstimates: repairEstimateOptions,
      rents: rentOptions,
      afterRepairValues: afterRepairValueOptions,
      closingCosts: closingCostsOptions,
      bidPrices: bidPriceOptions,
      id: selectedProformaInputsContainer.id,
      createdOn: selectedProformaInputsContainer.createdOn,
      isPostAcquisitionUnderwrite: selectedProformaInputsContainer.isPostAcquisitionUnderwrite,
      valuations: selectedProformaInputsContainer.valuations,
    };
  }
);

export const selectedPostAcqProformaInputsSelector = createSelector(
  // Selector for calculating the min/avg/max values of a valuation's selected comps
  [
    displayedPostAcqValuationSelector,
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.postAcqUWClientTask : state.postAcqUWState.postAcqUWClientTask,
    selectedPostAcqOpportunityCompsSelector,
    (state, viewingReadonlyMode) => viewingReadonlyMode ? state.postAcqUWState.underwriteModalState.selectedProformaInputsContainer : state.postAcqUWState.selectedProformaInputsContainer,
  ],
  (
    displayedValuation,
    selectedOpportunity,
    selectedOpportunityComps,
    selectedProformaInputsContainer
  ) => {

    if (
      !selectedOpportunity ||
      !displayedValuation ||
      !selectedProformaInputsContainer
    )
      return null;

    // Rent proforma radio items
    // Get full collection of comps, filter down to rent and only those that have been selected by the valuation user
    const rentComps = selectedOpportunityComps.filter(
      (c) =>
        c.compType === mapCompTypeToEnum('rent') &&
        displayedValuation.valuationsComps.find((vc) => vc.compId === c.id) !=
          null
    );
    // Then use service to calculate the min/avg/max and return a values object array as rent proforma radio items
    const rentOptions = proformaCompMinAvgMaxService.create(
      rentComps,
      'rents',
      'rent'
    );
    if (selectedProformaInputsContainer.rentProformaInputs) {
      selectedProformaInputsContainer.rentProformaInputs.forEach((item) =>
        rentOptions.push(item)
      );
    }

    // ARV proforma radio items
    // Get full collection of comps, filter down to sold and only those that have been selected by the valuation user
    const soldComps = selectedOpportunityComps.filter(
      (c) =>
        c.compType === mapCompTypeToEnum('sold') &&
        displayedValuation.valuationsComps.find((vc) => vc.compId === c.id) !=
          null
    );
    // Then use service to calculate the min/avg/max and return a values object array as ARV proforma radio items
    const afterRepairValueOptions = proformaCompMinAvgMaxService.create(
      soldComps,
      'afterRepairValues',
      'arv'
    );
    if (selectedProformaInputsContainer.arvProformaInputs) {
      selectedProformaInputsContainer.arvProformaInputs.forEach((item) =>
        afterRepairValueOptions.push(item)
      );
    }

    const lotValueOptions = [];
    if (selectedProformaInputsContainer.lotValueProformaInputs) {
      selectedProformaInputsContainer.lotValueProformaInputs.forEach((item) =>
        lotValueOptions.push(item)
      );
    }
    const asIsValueOptions = [];
    if (selectedProformaInputsContainer.asIsValueProformaInputs) {
      selectedProformaInputsContainer.asIsValueProformaInputs.forEach((item) =>
        asIsValueOptions.push(item)
      );
    }
    const reducedRentOptions = [];
    if (selectedProformaInputsContainer.reducedRentProformaInputs) {
      selectedProformaInputsContainer.reducedRentProformaInputs.forEach((item) =>
        reducedRentOptions.push(item)
      );
    }
    const reducedAfterRepairValueOptions = [];
    if (selectedProformaInputsContainer.reducedAfterRepairValueProformaInputs) {
      selectedProformaInputsContainer.reducedAfterRepairValueProformaInputs.forEach((item) =>
        reducedAfterRepairValueOptions.push(item)
      );
    }

    return {
      rents: rentOptions,
      afterRepairValues: afterRepairValueOptions,
      lotValues: lotValueOptions,
      asIsValues: asIsValueOptions,
      reducedRents: reducedRentOptions,
      reducedAfterRepairValues: reducedAfterRepairValueOptions,
      valuations: selectedProformaInputsContainer.valuations,
    };
  }
);

export const channelWorkflowGridFieldSettingsSelector = createSelector(
  [(state) => state.appState.appSettings],
  (appSettings) => {
    return appSettings.FieldSettings.filter(
      (fieldSetting) => fieldSetting.channelWorkflowGrid
    ).map((fieldSetting) => {
      const index =
        appSettings.FieldIndex.channelWorkflowGrid[fieldSetting.field];
      return {
        ...fieldSetting,
        index,
      };
    });
  }
);

export const nextTaskSelector = createSelector(
  [
    (state, module) => module === Constants.Module.POSTACQ ? state.postAcqUWState.postAcqUWClientTask : state.propertyUWState.propertyUWClientTxnOpp,
    queueBinsStateSelector,
    (state, module) => state.queueState[module].filteredTasks,
    (state, module) => state.queueState[module].queueFilterInput,
    showPostAcquisitionsSelector,
  ],
  (selectedOpportunity, queueBinsState, filteredTasks, queueFilterInput, showPostAcquisitions) => {
    if (!selectedOpportunity || !queueBinsState) return null;
    const tasks = queueBinsState.allTasks.tasks; // my COMPLETED and NOT COMPLETED tasks
    if (!tasks.map((task) => task.id).includes(selectedOpportunity.id)) {
      return null; // Selected task is not assigned to the user - no next task. (it will cause the [next] btn to be disabled)
    }
    let visibleTasks;
    if (queueFilterInput === '') {
      visibleTasks = tasks;
    } else {
      visibleTasks = tasks.filter((task) => filteredTasks.includes(task.id));
    }
    const selectedOpportunityIndex = visibleTasks.findIndex(
      (task) => task.id === selectedOpportunity.id
    );
    const checkTaskCompletedFn = Constants.Module.POSTACQ ? PostAcqCheckTaskCompleted : CheckTaskCompleted;
    const nextIncompleteTaskIndex = visibleTasks.findIndex(
      (task, index) =>
        index > selectedOpportunityIndex &&
        !checkTaskCompletedFn(task, showPostAcquisitions)
    );
    const nextTask = visibleTasks[nextIncompleteTaskIndex] || null;
    // console.log(nextTask)
    return nextTask;
  }
);

export const routerSelector = createSelector(
  (state) => state.router,
  (router) => router
);
//#region
